import React, { useState, useEffect } from "react";
import type { HeroSlide } from "@typings/index";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/16/solid";
interface HeroCarouselProps {
  slides: HeroSlide[];
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({ slides }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [timerKey, setTimerKey] = useState(0); // Force timer reset

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 7000);

    return () => clearInterval(timer);
  }, [slides.length, timerKey]); // Reset timer when timerKey changes

  const resetTimer = () => {
    setTimerKey((prev) => prev + 1); // Force timer reset
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    resetTimer();
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    resetTimer();
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    resetTimer();
  };

  return (
    <div className="relative h-screen md:h-[600px] overflow-hidden">
      {/* Slides */}
      <div className="relative h-full">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <div className="relative h-full">
              {/* Content */}
              <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat brightness-50"
                style={{
                  backgroundImage: `url(${import.meta.env.VITE_CLOUDFRONT_URL}${
                    slide.imageUrl
                  }`,
                }}
              />
              <div className="relative h-full flex items-center justify-center p-8">
                <button
                  onClick={goToPrevious}
                  className="lg:block hidden cursor-pointer bg-white bg-opacity-20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-opacity-30 transition-all duration-300 z-10"
                  aria-label="Previous slide"
                >
                  <ChevronLeftIcon className="w-6 h-6 text-primaryText" />
                </button>
                <div className="text-center text-white px-4 max-w-4xl mx-auto">
                  <h1 className="text-4xl md:text-6xl font-bold mb-4 animate-fade-in">
                    {slide.title}
                  </h1>
                  <p className="text-xl md:text-2xl mb-8 animate-slide-up">
                    {slide.subtitle}
                  </p>
                  <a
                    href={slide.ctaLink}
                    className="inline-block bg-primary text-white px-8 py-3 rounded-md font-semibold hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 animate-slide-up"
                  >
                    {slide.ctaText}
                  </a>
                </div>
                <button
                  onClick={goToNext}
                  className="lg:block hidden cursor-pointer bg-white bg-opacity-20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-opacity-30 transition-all duration-300 z-10"
                  aria-label="Next slide"
                >
                  <ChevronRightIcon className="w-6 h-6 text-primaryText" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Slide Indicators */}
      <div className="absolute flex flex-row w-full items-center px-6 justify-between bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <button
          onClick={goToPrevious}
          className="cursor-pointer lg:invisible bg-white bg-opacity-20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-opacity-30 transition-all duration-300 z-10"
          aria-label="Previous slide"
        >
          <ChevronLeftIcon className="w-6 h-6 text-primaryText" />
        </button>
        <div className="flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentSlide
                  ? "bg-primary w-8"
                  : "bg-white bg-opacity-50 hover:bg-opacity-75"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        <button
          onClick={goToNext}
          className="cursor-pointer lg:invisible bg-white bg-opacity-20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-opacity-30 transition-all duration-300 z-10"
          aria-label="Next slide"
        >
          <ChevronRightIcon className="w-6 h-6 text-primaryText" />
        </button>
      </div>
    </div>
  );
};

export default HeroCarousel;
